import { ApolloQueryResult, useQuery } from "@apollo/client";
import { HighlightOff } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ActBlueImportIntegration,
  ActionKitIntegration,
  AllIntegrationTypes,
  CampaignContactActBlueImportArgs,
  CampaignContactActionKitImportArgs,
  CampaignContactAudienceImportArgs,
  CampaignContactFollowUpImportArgs,
  CampaignContactGcsImportArgs,
  CampaignContactNgpVanImportArgs,
  CampaignContactNgpVanImportResult,
  CampaignContactUploadArgs,
  CampaignFieldsForEditorFragment,
  CancelTrackedJobMutation,
  CancelTrackedJobMutationVariables,
  GcsImportIntegration,
  GetCampaignQuery,
  ListAudiencesForSubaccountQuery,
  ListAudiencesForSubaccountQueryVariables,
  ListIntegrationsQuery,
  ListIntegrationsQueryVariables,
  NgpVanIntegration,
  ResetCampaignContactImportJobMutation,
  ResetCampaignContactImportJobMutationVariables,
  TrackedJobStatus,
  TrackedJobType,
} from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { LIST_AUDIENCES_FOR_SUBACCOUNT } from "~frontend/graphql/audience";
import {
  GET_CAMPAIGN_FOR_VIEW,
  RESET_CAMPAIGN_CONTACT_UPLOAD_JOB,
} from "~frontend/graphql/campaigns";
import { LIST_INTEGRATIONS } from "~frontend/graphql/integration";
import { CANCEL_TRACKED_JOB } from "~frontend/graphql/trackedJobs";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "../StepInstructions";
import { CampaignForEditor } from "../types";
import ActBlueImport from "./ActBlueImport";
import ActionKitImport from "./ActionKitImport";
import AudienceImport from "./AudienceImport";
import ContactSourceAccordion from "./ContactSourceAccordion";
import ContactSources, {
  ContactSource,
  ContactSourceWithDisplayInfo,
} from "./ContactSources";
import CsvUploadImport from "./CsvUploadImport";
import FollowUpImport from "./FollowUpImport";
import GCSImport from "./GCSImport";
import JobComplete from "./JobComplete";
import NgpVanImport from "./NgpVanImport";
import SubscriberImport from "./SubscriberImport";

export type ImportStatus = "PREPARING" | "IN_PROGRESS" | "DONE" | "FAILED";

function CancelJobControl({
  trackedJob,
}: {
  trackedJob: { id: string; cancelRequestedAt?: Date | null };
}): ReactElement {
  const [cancelTrackedJob, { loading, data }] = useMutation<
    CancelTrackedJobMutation,
    CancelTrackedJobMutationVariables
  >(CANCEL_TRACKED_JOB, {
    variables: { jobId: trackedJob.id },
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    onErrorSnackbarMessage: "Failed to cancel job, please try again",
  });

  if (trackedJob.cancelRequestedAt || data) {
    return <>Canceling job...</>;
  } else if (loading) {
    return <Loading />;
  } else {
    return (
      <>
        <Button onClick={() => cancelTrackedJob()}>
          <HighlightOff sx={{ marginRight: 0.5 }} />
          Cancel Upload
        </Button>
      </>
    );
  }
}

function contactImportJobIsInProgress(campaign: CampaignForEditor) {
  if (!campaign.contactImportJob) {
    return false;
  }

  return jobInProgress(campaign.contactImportJob);
}

function contactSourceForJob(
  job: CampaignFieldsForEditorFragment["contactImportJob"]
): ContactSource {
  if (!job) {
    return "CSV";
  }

  if (job.type === TrackedJobType.CampaignContactImport) {
    switch (job.__typename) {
      case "CampaignContactActionKitImportJob":
        return "ActionKitImport";
      case "CampaignContactGCSImportJob":
        return "GCSImport";
      case "CampaignContactFollowUpImportJob":
        return "FollowUpImport";
      case "CampaignContactActBlueImportJob":
        return "ActBlueImport";
      case "CampaignContactNgpVanImportJob":
        return "NgpVanImport";
      case "CampaignContactSubscriberImportJob":
        return "SubscriberImport";
      case "CampaignContactAudienceImportJob": {
        return job.args.audienceType === "SUBSCRIBERS"
          ? "SubscriberImport"
          : "AudienceImport";
      }
      default:
        return "CSV";
    }
  }

  // Note: this block is deprecated and is meant to handle jobs created before
  //       the contact import jobs were consolidated
  switch (job.type) {
    case TrackedJobType.CampaignContactActionKitImport:
      return "ActionKitImport";
    case TrackedJobType.CampaignContactGcsImport:
      return "GCSImport";
    case TrackedJobType.CampaignContactFollowUpImport:
      return "FollowUpImport";
    case TrackedJobType.CampaignContactActBlueImport:
      return "ActBlueImport";
    case TrackedJobType.CampaignContactNgpvanImport:
      return "NgpVanImport";
    case TrackedJobType.CampaignContactSubscriberImport:
      return "SubscriberImport";
    default:
      return "CSV";
  }
}

export default function Contacts({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
  refreshCampaign: () => Promise<ApolloQueryResult<GetCampaignQuery>>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const [contactSource, setContactSource] = useState<ContactSource>(() => {
    if (campaign.contactImportJob) {
      return contactSourceForJob(campaign.contactImportJob);
    } else if (campaign.integration?.__typename === "NgpVanIntegration") {
      return "NgpVanImport";
    } else if (campaign.usingOptInStrategy) {
      return "SubscriberImport";
    }

    return "CSV";
  });

  const multiAudienceEnabled = useFeatureFlag("enableMultiAudience");

  const [contactSourceAccordionExpanded, setContactSourceAccordionExpanded] =
    useState(!campaign.contactImportJob);

  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const jobWasInProgress = useRef(contactImportJobIsInProgress(campaign));
  useEffect(() => {
    if (jobWasInProgress.current !== contactImportJobIsInProgress(campaign)) {
      setContactSourceAccordionExpanded(false);
    }
    jobWasInProgress.current = contactImportJobIsInProgress(campaign);
  }, [campaign]);

  const importStatus = useMemo<ImportStatus>(() => {
    if (!campaign.contactImportJob) {
      return "PREPARING";
    }
    if (jobFailed(campaign.contactImportJob).failed) {
      return "FAILED";
    }
    if (campaign.contactImportJob && jobInProgress(campaign.contactImportJob)) {
      return "IN_PROGRESS";
    }
    return "DONE";
  }, [campaign]);

  const listAvailableIntegrationsResult = useQuery<
    ListIntegrationsQuery,
    ListIntegrationsQueryVariables
  >(LIST_INTEGRATIONS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const listAvailableAudiencesResult = useQuery<
    ListAudiencesForSubaccountQuery,
    ListAudiencesForSubaccountQueryVariables
  >(LIST_AUDIENCES_FOR_SUBACCOUNT, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const {
    availableActionKitIntegrations,
    availableActBlueImportIntegrations,
    availableNgpVanIntegrations,
    availableGcsImportIntegrations,
  } = useMemo(() => {
    const integrations = (listAvailableIntegrationsResult.data
      ?.listIntegrations || []) as AllIntegrationTypes[];

    return {
      availableActionKitIntegrations: integrations.filter(
        (integration: AllIntegrationTypes) =>
          integration.__typename === "ActionKitIntegration"
      ) as ActionKitIntegration[],
      availableActBlueImportIntegrations: integrations.filter(
        (integration: AllIntegrationTypes) =>
          integration.__typename === "ActBlueImportIntegration"
      ) as ActBlueImportIntegration[],
      availableNgpVanIntegrations: integrations.filter(
        (integration: AllIntegrationTypes) =>
          integration.__typename === "NgpVanIntegration"
      ) as NgpVanIntegration[],
      availableGcsImportIntegrations: integrations.filter(
        (integration: AllIntegrationTypes) =>
          integration.__typename === "GCSImportIntegration"
      ) as GcsImportIntegration[],
    };
  }, [listAvailableIntegrationsResult]);

  const availableNonSubscriberAudiences = useMemo(() => {
    const audiences =
      listAvailableAudiencesResult.data?.listAudiencesForSubaccount || [];
    return audiences.filter(a => a.type !== "SUBSCRIBERS" && !a.archivedAt);
  }, [listAvailableAudiencesResult]);

  const [resetCampaignContactImportJob, { loading: resettingImportJob }] =
    useMutation<
      ResetCampaignContactImportJobMutation,
      ResetCampaignContactImportJobMutationVariables
    >(RESET_CAMPAIGN_CONTACT_UPLOAD_JOB);

  const confirm = useConfirm();
  const snackbar = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();

  const readOnly = !!campaign.startedAt;

  const handleSelectContactSource = async (source: ContactSource) => {
    switch (importStatus) {
      case "PREPARING":
        setContactSource(source);
        break;
      case "IN_PROGRESS":
        break;
      case "DONE":
      case "FAILED":
        setContactSource(source);
        setContactSourceAccordionExpanded(true);
        break;
    }
  };

  const displayErrorToast = useCallback(
    (message: string, errors: unknown) => {
      console.error(errors);
      snackbar.enqueueSnackbar(`${message}: ${formatErrorMessage(errors)}`, {
        variant: "error",
      });
    },
    [snackbar, formatErrorMessage]
  );

  const resetContactSourceSettings = useCallback(() => {
    if (campaign.contactImportJob) {
      setContactSource(contactSourceForJob(campaign.contactImportJob));
    }
    setContactSourceAccordionExpanded(false);
  }, [campaign.contactImportJob]);

  function clearFileFunction(campaignId: string): () => void {
    return async () => {
      if ((campaign.contactImportJob?.result?.nContacts ?? 0) > 0) {
        const { confirmed } = await confirm({
          title: "Confirm clear contacts",
          description: "Are you sure? This will delete any existing contacts.",
        });
        if (!confirmed) {
          return;
        }
      }

      const { errors } = await resetCampaignContactImportJob({
        variables: {
          input: {
            campaignId,
          },
        },
      });

      if (errors) {
        displayErrorToast("Failed to reset contacts", errors);
      } else {
        setContactSourceAccordionExpanded(true);
      }
    };
  }

  const renderComponentForSource = () => {
    if (resettingImportJob) {
      return <Loading />;
    }

    const previousJobArgs =
      campaign.contactImportJob &&
      contactSource === contactSourceForJob(campaign.contactImportJob)
        ? campaign.contactImportJob.args
        : null;

    const previousJobResult =
      campaign.contactImportJob &&
      contactSource === contactSourceForJob(campaign.contactImportJob)
        ? campaign.contactImportJob.result
        : null;

    const resetContactSourceSettingsFn = campaign.contactImportJob
      ? resetContactSourceSettings
      : null;

    switch (contactSource) {
      case "CSV":
        return (
          <CsvUploadImport
            campaign={campaign}
            readOnly={readOnly}
            previousJobArgs={previousJobArgs as CampaignContactUploadArgs}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
      case "FollowUpImport":
        return (
          <FollowUpImport
            campaign={campaign}
            readOnly={readOnly}
            previousJobArgs={
              previousJobArgs as CampaignContactFollowUpImportArgs
            }
            displayErrorToast={displayErrorToast}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
      case "ActionKitImport":
        return (
          <ActionKitImport
            campaign={campaign}
            availableActionKitIntegrations={availableActionKitIntegrations}
            readOnly={readOnly}
            previousJobArgs={
              previousJobArgs as CampaignContactActionKitImportArgs
            }
            displayErrorToast={displayErrorToast}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
      case "GCSImport":
        return (
          <GCSImport
            campaign={campaign}
            availableGCSIntegrations={availableGcsImportIntegrations}
            readOnly={readOnly}
            previousJobArgs={previousJobArgs as CampaignContactGcsImportArgs}
            displayErrorToast={displayErrorToast}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
      case "SubscriberImport": {
        return (
          <SubscriberImport
            campaign={campaign}
            readOnly={readOnly}
            previousJobArgs={
              previousJobArgs as CampaignContactAudienceImportArgs
            }
          />
        );
      }
      case "AudienceImport": {
        return (
          <AudienceImport
            campaign={campaign}
            readOnly={readOnly}
            previousJobArgs={
              previousJobArgs as CampaignContactAudienceImportArgs
            }
          />
        );
      }
      case "ActBlueImport":
        return (
          <ActBlueImport
            campaign={campaign}
            availableActBlueImportIntegrations={
              availableActBlueImportIntegrations
            }
            readOnly={readOnly}
            previousJobArgs={
              previousJobArgs as CampaignContactActBlueImportArgs
            }
            displayErrorToast={displayErrorToast}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
      case "NgpVanImport":
        return (
          <NgpVanImport
            campaign={campaign}
            availableIntegrations={availableNgpVanIntegrations}
            readOnly={readOnly}
            previousJobArgs={previousJobArgs as CampaignContactNgpVanImportArgs}
            previousJobResult={
              previousJobResult as CampaignContactNgpVanImportResult
            }
            displayErrorToast={displayErrorToast}
            resetContactSourceSettings={resetContactSourceSettingsFn}
          />
        );
    }
  };

  function renderBottomButtons() {
    if (readOnly) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
          }}
          columnGap={1}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => goToNextSection()}
          >
            Continue
          </Button>
        </Box>
      );
    }

    if (
      importStatus === "FAILED" ||
      campaign.contactImportJob?.result?.nContacts === 0
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          columnGap={1}
        >
          {campaign.startedAt ? (
            <>&nbsp;</>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={clearFileFunction(campaign.id)}
            >
              Clear Settings / Retry
            </Button>
          )}
          <Button variant="contained" color="primary" disabled>
            Continue
          </Button>
        </Box>
      );
    }

    if (importStatus === "DONE") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          columnGap={1}
        >
          <Button
            variant="contained"
            color="tertiary"
            onClick={clearFileFunction(campaign.id)}
          >
            Clear Contacts / Reload
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => goToNextSection()}
          >
            Continue
          </Button>
        </Box>
      );
    }

    return null;
  }

  const availableSources: ContactSourceWithDisplayInfo[] = [
    { contactSource: "CSV", label: "CSV Upload" },
    { contactSource: "FollowUpImport", label: "Follow Up Campaign" },
    {
      contactSource: "ActionKitImport",
      label: "ActionKit Sync",
      disabled: !availableActionKitIntegrations.length,
      disabledReason: !availableActionKitIntegrations.length
        ? "no integration set up"
        : undefined,
    },
    {
      contactSource: "GCSImport",
      label: "GCS Import",
      disabled: !availableGcsImportIntegrations.length,
      disabledReason: !availableGcsImportIntegrations.length
        ? "no integration set up"
        : undefined,
    },
    {
      contactSource: "ActBlueImport",
      label: "ActBlue Sync",
      disabled:
        !availableActBlueImportIntegrations.length ||
        ngpVanCampaignIntegrationEnabled,
      disabledReason: !availableActBlueImportIntegrations.length
        ? "no integration set up"
        : ngpVanCampaignIntegrationEnabled
          ? "cannot be used with NGP VAN / EveryAction campaigns"
          : undefined,
    },
    {
      contactSource: "SubscriberImport",
      label: "Subscribers",
      disabled:
        !campaign.usingOptInStrategy || ngpVanCampaignIntegrationEnabled,
      disabledReason: !campaign.usingOptInStrategy
        ? "campaign must be using a short code number"
        : ngpVanCampaignIntegrationEnabled
          ? "cannot be used with NGP VAN / EveryAction campaigns"
          : undefined,
    },
    {
      contactSource: "NgpVanImport",
      label: "NGP VAN / EveryAction",
      disabled:
        !availableNgpVanIntegrations.length ||
        !ngpVanCampaignIntegrationEnabled,
      disabledReason: !availableNgpVanIntegrations.length
        ? "no integration set up"
        : !ngpVanCampaignIntegrationEnabled
          ? "no integration selected for this campaign"
          : undefined,
    },
  ];

  if (multiAudienceEnabled) {
    const hasAvailableAudiences = availableNonSubscriberAudiences.length > 0;
    availableSources.push({
      contactSource: "AudienceImport",
      label: "My Audiences",
      disabled: !hasAvailableAudiences,
      disabledReason: hasAvailableAudiences
        ? undefined
        : "no audiences created",
    });
  }

  const currentContactSourceLabel =
    availableSources.find(s => s.contactSource === contactSource)?.label ?? "";

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <StepInstructions header="Load Contacts" />
      </Box>

      {!campaign.startedAt && (
        <ContactSources
          selectedSource={contactSource}
          availableSources={availableSources}
          onSelectSource={handleSelectContactSource}
          disabled={!!campaign.startedAt || importStatus === "IN_PROGRESS"}
        />
      )}
      {ngpVanCampaignIntegrationEnabled &&
        !!availableActBlueImportIntegrations.length && (
          <Typography
            variant="body2"
            sx={{ color: palette.darkGray, marginBottom: theme.spacing(3) }}
          >
            Note: You cannot load contacts from ActBlue to this campaign because
            you are using a VAN integration, which is not compatible with
            ActBlue contacts (this does not impact your ability to use an
            ActBlue link in your initial message, and you will still be able to
            track donations if you have an ActBlue integration set up). To load
            contacts from ActBlue, you must remove your VAN integration on the{" "}
            <InternalLink
              to={`/subaccounts/${subaccount.id}/campaigns/${campaign.shortId}/edit/basics`}
            >
              Basics step
            </InternalLink>
            .
          </Typography>
        )}

      {ngpVanCampaignIntegrationEnabled && campaign.usingOptInStrategy && (
        <Typography
          variant="body2"
          sx={{ color: palette.darkGray, marginBottom: theme.spacing(3) }}
        >
          Note: You cannot load contacts directly from your subscriber list
          because you are using a VAN integration, which is not compatible with
          importing directly from your subscriber list. To load contacts from
          ActBlue, you must remove your VAN integration on the{" "}
          <InternalLink
            to={`/subaccounts/${subaccount.id}/campaigns/${campaign.shortId}/edit/basics`}
          >
            Basics step
          </InternalLink>
          .
        </Typography>
      )}
      <Box sx={{ pb: 3 }}>
        {importStatus === "IN_PROGRESS" ? (
          <Loading
            message={
              <>
                <span>
                  Importing{" "}
                  {campaign?.contactImportJob?.__typename ===
                  "CampaignContactUploadJob"
                    ? (
                        campaign.contactImportJob
                          .args as CampaignContactUploadArgs
                      ).upload.fileName
                    : ""}
                  ...{" "}
                </span>
                <br />
                <em>
                  You can leave and the import will continue in the background
                </em>
                <br />
                <CancelJobControl trackedJob={campaign.contactImportJob!} />
              </>
            }
          />
        ) : (
          <ContactSourceAccordion
            label={currentContactSourceLabel}
            expanded={contactSourceAccordionExpanded}
            setExpanded={
              importStatus == "PREPARING"
                ? undefined
                : setContactSourceAccordionExpanded
            }
          >
            {renderComponentForSource()}
          </ContactSourceAccordion>
        )}
        {["DONE", "FAILED"].includes(importStatus) && (
          <JobComplete campaign={campaign} />
        )}
      </Box>

      {renderBottomButtons()}
    </>
  );
}

export function validateContacts({
  campaign,
}: {
  campaign: CampaignForEditor;
}): { title: string; message: ReactNode }[] {
  if (
    !campaign.contactImportJob ||
    campaign.contactImportJob.status !== TrackedJobStatus.Done ||
    !campaign.contactImportJob.result
  ) {
    return [
      {
        title: "Upload Contacts",
        message: "You must upload contacts before starting your campaign.",
      },
    ];
  }

  if (campaign.contactImportJob.result.nContacts === 0) {
    return [
      {
        title: "Zero Contacts",
        message:
          "Your contact upload resulted in 0 contacts due to invalid numbers, Opt Outs, and/or landlines.",
      },
    ];
  }

  if (
    campaign.suppressedContactCount >=
    campaign.contactImportJob.result.nContacts
  ) {
    return [
      {
        title: "All Contacts Suppressed",
        message: "Your campaign has no remaining contacts after suppressions.",
      },
    ];
  }

  return [];
}
